/**
 * LennyReviewQueue — NiV list of Lenny (AI support) exchanges that need a
 * human look: Lenny did not know the answer ("unknown") or rated the exchange
 * ≥ 80 for admin attention ("attention"). Written by the aiSupportChat Cloud
 * Function into niv_lenny_reviews; NiV marks them handled here.
 *
 * Props:
 *  - compact: briefing-style (first N open items, no resolved toggle)
 *  - maxItems: cap when compact
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Robot,
  Buildings,
  User,
  Check,
  ArrowCounterClockwise,
  CaretLeft,
  Question,
  WarningCircle,
} from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import {
  useGetLennyReviewsQuery,
  useSetLennyReviewStatusMutation,
} from "../../../../store/services/nivApi";
import { selectTherapistName } from "../../../../store/slices/authSlice";
import { useToast } from "../../../../contexts/ToastContext";
import LennyText from "../shared/LennyText";
import "./LennyReviewQueue.css";

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const FLAG_META = {
  unknown: { label: "לני לא ידע", icon: Question, cls: "unknown" },
  attention: { label: "דורש מנהל", icon: WarningCircle, cls: "attention" },
};

function LennyReviewQueue({ compact = false, maxItems = 6 }) {
  const navigate = useNavigate();
  const adminName = useSelector(selectTherapistName);
  const { showSuccess, showError } = useToast();
  const [showResolved, setShowResolved] = useState(false);

  const { data: reviews = [], isLoading } = useGetLennyReviewsQuery(
    { status: showResolved ? "all" : "open" },
    { refetchOnMountOrArgChange: 60 }
  );
  const [setStatus, { isLoading: isUpdating }] = useSetLennyReviewStatusMutation();

  const openReviews = useMemo(() => reviews.filter((r) => r.status === "open"), [reviews]);
  const list = useMemo(() => {
    const src = showResolved ? reviews : openReviews;
    return compact ? src.slice(0, maxItems) : src;
  }, [reviews, openReviews, showResolved, compact, maxItems]);

  const handleSetStatus = async (review, status) => {
    const result = await setStatus({ reviewId: review.id, status, resolvedBy: adminName || "" });
    if (result?.error) {
      showError("לא הצלחנו לעדכן את הפריט. נסה שוב.");
      return;
    }
    showSuccess(status === "resolved" ? "סומן כטופל" : "הפריט נפתח מחדש");
  };

  const openClinic = (clinicId) =>
    navigate(`/dashboard/niv/clinic/${clinicId}`, { state: { tab: "lenny" } });

  if (compact && !isLoading && openReviews.length === 0) return null;

  return (
    <section className={`nlq-section ${compact ? "nlq-compact" : ""}`}>
      <div className="nlq-head">
        <div className="nlq-head-title">
          <Robot size={20} weight="fill" />
          <h4>
            שאלות ללני לבדיקה
            {openReviews.length > 0 ? ` (${openReviews.length})` : ""}
          </h4>
        </div>
        {compact ? (
          <button
            type="button"
            className="nlq-head-link"
            onClick={() => navigate("/dashboard/niv/support")}
          >
            לכל השאלות
            <CaretLeft size={12} />
          </button>
        ) : (
          <label className="nlq-toggle">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
            />
            הצג גם טופלו
          </label>
        )}
      </div>

      {isLoading ? (
        <ContentLoader size="sm" compact message="טוען…" />
      ) : list.length === 0 ? (
        <div className="nlq-empty">
          <Robot size={28} weight="light" />
          לני מסתדר לבד — אין שאלות שממתינות לבדיקה
        </div>
      ) : (
        <div className="nlq-list">
          {list.map((review) => {
            const flag = FLAG_META[review.flagType] || FLAG_META.attention;
            const FlagIcon = flag.icon;
            const resolved = review.status === "resolved";
            return (
              <article
                key={review.id}
                className={`nlq-card nlq-${flag.cls} ${resolved ? "nlq-resolved" : ""}`}
              >
                <div className="nlq-card-head">
                  <div className="nlq-badges">
                    <span className={`nlq-flag nlq-flag-${flag.cls}`}>
                      <FlagIcon size={13} weight="fill" />
                      {flag.label}
                      {review.flagType !== "unknown" && review.adminAttention != null
                        ? ` · ${review.adminAttention}%`
                        : ""}
                    </span>
                    {resolved && <span className="nlq-flag nlq-flag-done">טופל</span>}
                  </div>
                  <span className="nlq-date" dir="ltr">
                    {formatDateTime(review.createdAt)}
                  </span>
                </div>

                <div className="nlq-meta">
                  <button
                    type="button"
                    className="nlq-clinic-link"
                    onClick={() => openClinic(review.clinicId)}
                    title="פתח את היסטוריית לני של הקליניקה"
                  >
                    <Buildings size={12} weight="fill" />
                    {review.clinicName || review.clinicId}
                  </button>
                  {review.therapistName && (
                    <span>
                      <User size={12} weight="fill" />
                      {review.therapistName}
                    </span>
                  )}
                </div>

                {review.reason && <p className="nlq-reason">{review.reason}</p>}

                <div className="nlq-exchange">
                  <div className="nlq-bubble nlq-bubble-user">
                    <span className="nlq-bubble-label">שאלה</span>
                    <LennyText content={review.question} />
                  </div>
                  <div className="nlq-bubble nlq-bubble-assistant">
                    <span className="nlq-bubble-label">לני</span>
                    <LennyText content={review.answer} />
                  </div>
                </div>

                <div className="nlq-actions">
                  {resolved ? (
                    <button
                      type="button"
                      className="nlq-btn nlq-btn-secondary"
                      disabled={isUpdating}
                      onClick={() => handleSetStatus(review, "open")}
                    >
                      <ArrowCounterClockwise size={14} />
                      פתח מחדש
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="nlq-btn nlq-btn-primary"
                      disabled={isUpdating}
                      onClick={() => handleSetStatus(review, "resolved")}
                    >
                      <Check size={14} weight="bold" />
                      טופל
                    </button>
                  )}
                  <button
                    type="button"
                    className="nlq-btn nlq-btn-secondary"
                    onClick={() => openClinic(review.clinicId)}
                  >
                    היסטוריית לני
                    <CaretLeft size={12} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default LennyReviewQueue;
